import { Box, Icon, Typography } from "@mui/material";
import { useStyles } from "./styles";
import Chart from "react-apexcharts";
import { BLACK } from "../../utils/colors";
import { useMemo } from "react";

interface CardGraphProps {
  title?: string;
  text?: string;
  data?: any;
  data_type?: string;
}

interface Config {
  series: any;
  bgBoxIcon: string;
  bgIcon: string;
  icon: string;
  heightCard?: number;
  chart_type?: "line" | "area" | "bar";
  orientationLabel?: "vertical" | "horizontal";
  columnWidth?: string;
  positionLabel?: string;
  dataLabelOffSetY?: number;
  dataLabelOffSetX?: number;
}

export function CardGraph(props: CardGraphProps) {
  const { title, text, data, data_type } = props;
  const styles = useStyles();

  const config: Config = useMemo(() => {
    let configObject = {
      chart_type:
        data[0]?.data?.length > 1 ? "area" : ("bar" as "line" | "area" | "bar"),
    };
    switch (data_type) {
      case "REACH":
        return {
          ...configObject,
          bgBoxIcon: "#E6FFFB",
          bgIcon: "#13C2C2",
          icon: "people",
          series: data,
        };
      case "IMPRESSIONS":
        return {
          ...configObject,
          bgBoxIcon: "#F6FFED",
          bgIcon: "#52C41A",
          icon: "visibility",
          series: data,
        };
      case "ADS_FREQUENCY":
        return {
          ...configObject,
          bgBoxIcon: "#E6F7FF",
          bgIcon: "#1890FF",
          icon: "access_time",
          series: data,
        };
      case "ADS_CLICKS":
        return {
          ...configObject,
          bgBoxIcon: "#FFF1F0",
          bgIcon: "#F5222D",
          icon: "ads_clicks",
          series: data,
        };
      case "ADS_AGE":
        return {
          bgBoxIcon: "#F5FCDB",
          bgIcon: "#F0E717",
          icon: "calendar_month",
          chart_type: "bar",
          series: data,
          orientationLabel: "vertical",
          positionLabel: "top",
          columnWidth: "70%",
          dataLabelOffSetY: 2,
        };
      case "ADS_GENDER":
        return {
          bgBoxIcon: "#FFEEFF",
          bgIcon: "#8D63B2",
          icon: "calendar_month",
          chart_type: "bar",
          series: data,
          orientationLabel: "vertical",
          positionLabel: "top",
          columnWidth: "70%",
          dataLabelOffSetY: 2,
        };
      case "ADS_DEVICE":
        return {
          bgBoxIcon: "#FDE9DC",
          bgIcon: "#764924",
          icon: "devices",
          chart_type: "bar",
          series: data,
          orientationLabel: "vertical",
          positionLabel: "top",
          columnWidth: "70%",
          dataLabelOffSetY: 2,
        };
      case "ADS_PLATFORM":
        return {
          bgBoxIcon: "#C9DBFA",
          bgIcon: "#5C3BBF",
          icon: "groups",
          chart_type: "bar",
          series: data,
          positionLabel: "top",
        };

      case "ENGAGEMENT":
        return {
          ...configObject,
          bgBoxIcon: "#E6F7FF",
          bgIcon: "#1890FF",
          icon: "insights",
          series: data,
        };
      case "REACTIONS":
        return {
          ...configObject,
          bgBoxIcon: "#FFF1F0",
          bgIcon: "#F5222D",
          icon: "favorite_border",
          series: data,
        };
      case "COMMENTS":
        return {
          ...configObject,
          bgBoxIcon: "#F5FCDB",
          bgIcon: "#F0E717",
          icon: "message",
          series: data,
        };
      case "SENT":
        return {
          ...configObject,
          bgBoxIcon: "#E6F7FF",
          bgIcon: "#1890FF",
          icon: "insights",
          series: data,
        };
      case "READ":
        return {
          ...configObject,
          bgBoxIcon: "#F6FFED",
          bgIcon: "#52C41A",
          icon: "visibility",
          series: data,
        };
      case "UNREAD":
        return {
          ...configObject,
          bgBoxIcon: "#F6FFED",
          bgIcon: "#d1d1d1",
          icon: "visibility",
          series: data,
        };
      case "WAIT":
        return {
          ...configObject,
          bgBoxIcon: "#F5FCDB",
          bgIcon: "#F0E717",
          icon: "timer",
          series: data,
        };
      case "ERROR":
        return {
          ...configObject,
          bgBoxIcon: "#FFF1F0",
          bgIcon: "#F5222D",
          icon: "error",
          series: data,
        };
      case "RECEIVED":
        return {
          ...configObject,
          bgBoxIcon: "#E6FFFB",
          bgIcon: "#13C2C2",
          icon: "insights",
          series: data,
        };
      default:
        return {
          ...configObject,
          bgBoxIcon: "#E6FFFB",
          bgIcon: "#13C2C2",
          icon: "insights",
          series: data,
        };
    }
  }, [data, data_type]);
  return (
    <Box sx={{ border: "1px solid #F5F5F5", borderRadius: 2, p: 1 }}>
      <Box display="flex" flexDirection="row" gap={1} alignItems="center">
        <Box
          className={styles.iconContent}
          sx={{
            background: config.bgBoxIcon,
          }}
        >
          <Icon
            sx={{
              color: config.bgIcon,
            }}
          >
            {config.icon}
          </Icon>
        </Box>
        <Box>
          <Typography
            fontWeight={500}
            fontSize={14}
            lineHeight="22px"
            color={BLACK}
          >
            {title || ""}
          </Typography>
          <Typography
            fontWeight={500}
            fontSize="12px"
            lineHeight="18px"
            color={"#B0B0B0"}
          >
            {text || "Comentários"}
          </Typography>
        </Box>
      </Box>

      <Chart
        height={config.heightCard ?? 325}
        series={config.series}
        type={config.chart_type}
        options={{
          plotOptions: {
            bar: {
              horizontal: false,
              rangeBarOverlap: false,
              columnWidth: config?.columnWidth ?? "70%",
              dataLabels: {
                orientation: config?.orientationLabel ?? "horizontal",
                position: config?.positionLabel,
              },
            },
          },
          dataLabels: {
            enabled: true,
            offsetX: config.dataLabelOffSetX ?? 0,
            offsetY: config?.dataLabelOffSetY ?? 0,
            style: {
              colors: ["black"],
            },
          },
          chart: {
            fontFamily: "Montserrat, normal",
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: false,
            },
            foreColor: "#616161",
          },
          grid: {
            show: true,
          },
          tooltip: {
            enabled: true,
            fillSeriesColor: true,
            x: {
              show: true,
            },
          },
          stroke: {
            show: true,
            curve: "straight",
            width: 2,
          },
          yaxis: {
            show: true,
          },
          xaxis: {
            labels: {
              show: true,
            },
            type: "category",
            axisBorder: {
              show: true,
              color: "#FFF",
            },
            axisTicks: {
              color: "#FFF",
            },
          },
          colors: [config.bgIcon],
          /*fill: {
            opacity: 1,
            type: "gradient",
            gradient: {
              shade: "dark",
              opacityFrom: 0.7,
              opacityTo: 0.3,
            },
          },*/
        }}
      />
      {config.series[0]?.data?.length === 0 && (
        <Typography
          fontWeight={500}
          fontSize={14}
          lineHeight="22px"
          color={BLACK}
        >
          Nenhum dado encontrado.
        </Typography>
      )}
    </Box>
  );
}
